// ---------------------------------------------------------------------------
// App-wide constants — SCREAMING_SNAKE_CASE per CLAUDE.md conventions.
// ---------------------------------------------------------------------------

export {
  DATASET_CATALOG_GENERIC_TYPE,
  DATASET_CATALOG_NEW_WINDOW_DAYS,
  DATASET_CATALOG_TYPE_ORDER,
} from './datasetCatalog.js';

/** Duration (ms) to show "Copied!" feedback after a clipboard write. */
export const COPY_TIMEOUT_MS = 1500;

/** Duration (ms) to cache fetched data before re-fetching. */
export const CACHE_MS = 60 * 60 * 1000; // 1 hour

/** Maximum UTF-8 payload buffered for a parsed text-file view. */
export const MAX_TEXT_FILE_DOWNLOAD_SIZE_BYTES = 10 * 1024 * 1024;

/** Number of rows for inline-edit description textareas. */
export const EDIT_DESCRIPTION_ROWS = 2;

/** Fallback error message when updating an entry fails. */
export const FAILED_TO_UPDATE_ENTRY = 'Failed to update entry';

/** localStorage keys used across the app. */
export const STORAGE_KEYS = {
  COLOR_SCHEME: 'ai-studio-color-scheme',
  SIGS_BEARER_TOKEN: 'sigs-bearer-token',
  SIGS_SAVED_CONFIGS: 'sigs-saved-configs',
} as const;

export const SYNTHETIC_TENANT_GUIDANCE_URL =
  'https://eng.ms/docs/experiences-devices/cap-microsoft-teams/fundamentals-engsys-support/teams-ai-studio/teams-ai-studio/content/framework/synthetic-tenants-emp';

export const SYNTHETIC_TENANT_ADVANCED_GUIDANCE_URL =
  'https://eng.ms/docs/experiences-devices/cap-microsoft-teams/fundamentals-engsys-support/teams-ai-studio/teams-ai-studio/content/framework/synthetic-tenants-emp-advanced';

export const SYNTHETIC_TENANT_AUGLOOP_ONBOARDING_URL =
  'https://eng.ms/docs/experiences-devices/cap-microsoft-teams/fundamentals-engsys-support/teams-ai-studio/teams-ai-studio/content/framework/synthetic-tenants-augloop-onboarding-skill';

export const WRITING_EVALS_SYNTHETIC_TENANTS_URL =
  'https://eng.ms/docs/experiences-devices/cap-microsoft-teams/fundamentals-engsys-support/teams-ai-studio/teams-ai-studio/content/framework/writing-evals-synthetic-tenants';

export const OFFICIAL_TENANT_STATUS_URL =
  'https://seval.microsoft.com/tenant/detail/Teams_EcoSync_V4_Clean/2.0';

// ── Datasets ──────────────────────────────────────────────────────────────────

/** Available layouts for browsing datasets. */
export const DATASET_VIEW_MODE = {
  TILES: 'tiles',
  LIST: 'list',
} as const;

export type DatasetViewMode =
  (typeof DATASET_VIEW_MODE)[keyof typeof DATASET_VIEW_MODE];

/** Constant list of dataset data-type labels used in the Datasets filter UI. */
export const DATASET_DATA_TYPES = [
  'All types',
  'Generic',
  'Meeting Data',
  'Chat Data',
  'File Data',
  'Annotation Data',
] as const;

export type DatasetDataType = (typeof DATASET_DATA_TYPES)[number];

/**
 * Sensitivity label options shown when Access Level is Restricted.
 * Public access always uses 'general' and shows no UI.
 */
export const DATASET_RESTRICTED_SENSITIVITY_LABELS = [
  { label: 'Confidential', value: 'confidential' },
  { label: 'Highly Confidential', value: 'highlyConfidential' },
] as const;

/** Data-type labels available when creating a dataset — same as the filter, minus the sentinel. */
export type DatasetCreateDataType =
  | 'Generic'
  | 'Meeting Data'
  | 'Chat Data'
  | 'File Data'
  | 'Annotation Data';

// Derived at runtime from DATASET_DATA_TYPES so the two lists stay in sync.
export const DATASET_CREATE_DATA_TYPES: readonly DatasetCreateDataType[] =
  DATASET_DATA_TYPES.filter(
    (t): t is DatasetCreateDataType => t !== 'All types',
  );

// ── Meeting data-entry fields ─────────────────────────────────────────────────

/** Data-type label that unlocks the Meeting-specific entry fields. */
export const MEETING_DATA_TYPE_LABEL: DatasetCreateDataType = 'Meeting Data';

/** Raw API `dataType` discriminator carried on meeting data entries (GET). */
export const MEETING_DATA_ENTRY_TYPE = 'meetingData';

/** Keys of the meeting-specific scalar fields stored on a data entry. */
export type MeetingEntryFieldKey =
  | 'meetingTitle'
  | 'language'
  | 'meetingDate'
  | 'organizerId';

/** Describes how a single meeting-specific field is captured and displayed. */
export interface MeetingEntryFieldDescriptor {
  key: MeetingEntryFieldKey;
  label: string;
  placeholder?: string;
  /** Renders the input as a native date picker when true. */
  isDate?: boolean;
}

/**
 * Meeting-specific fields shown in the Add Data Entry dialog and rendered back
 * in the entry detail view. Order is significant for the UI.
 */
export const MEETING_ENTRY_FIELDS: readonly MeetingEntryFieldDescriptor[] = [
  {
    key: 'meetingTitle',
    label: 'Meeting Title',
    placeholder: 'e.g. Weekly Sync',
  },
  { key: 'language', label: 'Language', placeholder: 'e.g. en-us' },
  { key: 'meetingDate', label: 'Meeting Date', isDate: true },
  { key: 'organizerId', label: 'Organizer ID', placeholder: 'AAD object ID' },
] as const;

/** Usage-restriction values accepted by the DataHub API (camelCase string enum). */
export type DatasetUsageRestriction = 'evalOnly' | 'unrestricted';

/** Dropdown options for usage restriction on dataset creation. */
export const DATASET_USAGE_RESTRICTION_OPTIONS: readonly {
  label: string;
  value: DatasetUsageRestriction | '';
}[] = [
  { label: 'Not specified', value: '' },
  { label: 'Eval only', value: 'evalOnly' },
  { label: 'Unrestricted', value: 'unrestricted' },
];

/** Retention-option values accepted by the DataHub API (camelCase string enum). */
export type DatasetRetentionOption = 'none' | 'days14' | 'days30' | 'year1';

/** Default retention value — no automatic expiry. */
export const DATASET_RETENTION_DEFAULT: DatasetRetentionOption = 'none';

/** Dropdown options for retention on dataset creation. */
export const DATASET_RETENTION_OPTIONS: readonly {
  label: string;
  value: DatasetRetentionOption;
}[] = [
  { label: 'No automatic expiry', value: 'none' },
  { label: '14 days', value: 'days14' },
  { label: '30 days', value: 'days30' },
  { label: '1 year', value: 'year1' },
];

/** Lookup from usage-restriction value to its display label (Overview tab). */
export const DATASET_USAGE_RESTRICTION_LABELS: Readonly<
  Record<string, string>
> = Object.fromEntries(
  DATASET_USAGE_RESTRICTION_OPTIONS.filter((o) => o.value !== '').map((o) => [
    o.value,
    o.label,
  ]),
);

/** Lookup from retention value to its display label (Overview tab). */
export const DATASET_RETENTION_LABELS: Readonly<Record<string, string>> =
  Object.fromEntries(DATASET_RETENTION_OPTIONS.map((o) => [o.value, o.label]));

/** Access-level filter options used in the Datasets filter UI. */
export const DATASET_ACCESS_FILTERS = ['All', 'Public', 'Restricted'] as const;

export type DatasetAccessFilter = (typeof DATASET_ACCESS_FILTERS)[number];

/** Ownership filter options used in the Datasets filter UI. */
export const DATASET_OWNERSHIP_FILTERS = [
  'All Datasets',
  'My Datasets',
] as const;

export type DatasetOwnershipFilter = (typeof DATASET_OWNERSHIP_FILTERS)[number];

// ── Dataset Entitlement (Core Identity) ───────────────────────────────────────

/** Core Identity entitlement URL for the test environment. */
export const DATASET_ENTITLEMENT_URL_TEST =
  'https://coreidentity.microsoft.com/manage/Entitlement/entitlement/datalaketest-z4c3';

/** Core Identity entitlement URL for SDF/prod environments. */
export const DATASET_ENTITLEMENT_URL_PROD =
  'https://coreidentity.microsoft.com/manage/Entitlement/entitlement/capevalsdata-cxcv';

/** Core Identity entitlement name for the test environment. */
export const DATASET_ENTITLEMENT_NAME_TEST = 'DataLakeTest-GroupAccess';

/** Core Identity entitlement name for SDF/prod environments. */
export const DATASET_ENTITLEMENT_NAME_PROD = 'CAPS-Evals-DataLake';

// ── Permissions / Access Control ──────────────────────────────────────────────

export const PERMISSIONS_PATH = 'permissions';
export const PERMISSIONS_PAGE_TITLE = 'Role Management';
export const PERMISSIONS_PAGE_DESCRIPTION =
  'Configure tenant level security groups to assign datalake roles for users.';
export const PERMISSIONS_INFO_TOOLTIP =
  'Data Admin role is managed via OAuth token claims. TeamEvalDataHub.Admin role is needed for user to be a datalake admin';
export const PERMISSIONS_SAVE_BUTTON = 'Save Tenant Roles';
export const PERMISSIONS_ACCESS_DENIED_TITLE = 'Access Denied';
export const PERMISSIONS_ACCESS_DENIED_BODY =
  'You need the Datalake Admin role to manage tenant group roles.';

export const TENANT_ROLE_LABEL_DATA_CREATOR = 'Data Creator';
export const TENANT_ROLE_LABEL_DATA_USER = 'Data User';

export const ACG_PROVIDER_AAD = 'aad';
export const ACG_PICKER_SEARCH_PLACEHOLDER = 'Search by group name';
export const ACG_PICKER_GROUP_LABEL = 'AAD Group';
export const ACG_PICKER_GROUP_ID_HINT = 'Group ID (auto-filled)';
export const ACG_PICKER_ADD_BUTTON = 'Add';
export const ACG_PICKER_GROUPS_SUFFIX = 'groups';

// ── GroupSearch (shared, provider-aware search component) ─────────────────────

export const GROUP_SEARCH_PROVIDER_AAD = 'aad';
export const GROUP_SEARCH_PROVIDER_APP = 'app';
export const GROUP_SEARCH_AAD_LABEL = 'AAD Group';
export const GROUP_SEARCH_APP_LABEL = 'App/Service';
export const GROUP_SEARCH_AAD_PLACEHOLDER = 'Search AAD group by name...';
export const GROUP_SEARCH_APP_NAME_PLACEHOLDER = 'App name...';
export const GROUP_SEARCH_APP_ID_PLACEHOLDER = 'App ID (client ID)...';
export const GROUP_SEARCH_GROUP_ID_HINT = 'Group ID (auto-filled)';
export const GROUP_SEARCH_ADD_BUTTON = 'Add';

/** API match-mode values for POST /datasets/{id}/data/search/by-tags. */
export const TAG_SEARCH_MATCH_MODE_AND = 0 as const;
export const TAG_SEARCH_MATCH_MODE_OR = 1 as const;

/** Page size for GET /datasets/{id}/participants/data (API range 1-100). */
export const PARTICIPANT_SEARCH_PAGE_SIZE = 20;

// ── Participant filter (meeting datasets only) ────────────────────────────────
export const PARTICIPANT_FILTER_SHOW_LABEL = 'Filter by Participant';
export const PARTICIPANT_FILTER_HIDE_LABEL = 'Hide Participant Filter';
export const PARTICIPANT_FILTER_TOGGLE_ARIA = 'Toggle participant filter';
export const PARTICIPANT_SEARCH_PLACEHOLDER =
  'Email or alias (e.g. user@microsoft.com)';
export const PARTICIPANT_SEARCH_BUTTON_LABEL = 'Search';
export const PARTICIPANT_SEARCH_BUTTON_BUSY_LABEL = 'Searching...';
export const PARTICIPANT_SEARCH_CLEAR_LABEL = 'Clear';
export const PARTICIPANT_SEARCH_LOAD_MORE_LABEL = 'Load more';
export const PARTICIPANT_SEARCH_LOAD_MORE_BUSY_LABEL = 'Loading...';
export const PARTICIPANT_HARD_DELETE_ALL_LABEL =
  'Hard Delete All by Participant';
export const PARTICIPANT_SEARCH_EMPTY_MESSAGE =
  'No data entries found for this participant.';

/** Tab keys for the dataset detail panel. */
export const DATASET_DETAIL_TABS = {
  OVERVIEW: 'overview',
  VERSIONS: 'versions',
  PERMISSIONS: 'permissions',
  ATTRIBUTES: 'attributes',
} as const;

/** Label shown inside buttons that are planned but not yet implemented. */
export const COMING_SOON_LABEL = 'Coming soon';

/** Max attribute definitions to fetch in a single request. */
export const ATTRIBUTES_SEARCH_PAGE_SIZE = 100;

// ── Dataset Permissions Tab ───────────────────────────────────────────────────

export const DATASET_PERMISSIONS_ROLE_OWNER = 'Owner';
export const DATASET_PERMISSIONS_ROLE_CONTRIBUTOR = 'Contributor';
export const DATASET_PERMISSIONS_ROLE_ANNOTATOR = 'Annotator';
export const DATASET_PERMISSIONS_ROLE_CONSUMER = 'Consumer';
export const DATASET_PERMISSIONS_ROLE_RESTRICTED_CONSUMER =
  'Restricted Consumer';
export const DATASET_PERMISSIONS_SAVE_BUTTON = 'Save Permissions';

// Dataset role values returned by GET /datasets/grouprole/{dataSetId}/me
export const DATASET_ROLE_OWNER = 'owner' as const;
export const DATASET_ROLE_CONTRIBUTOR = 'contributor' as const;
export const DATASET_ROLE_ANNOTATOR = 'annotator' as const;
export const DATASET_ROLE_CONSUMER = 'consumer' as const;
export const DATASET_ROLE_RESTRICTED_CONSUMER = 'restrictedConsumer' as const;

// Dataset usage dashboard (Overview tab). The read API defaults to the last 30
// days at daily granularity; we query with those defaults.
export const DATASET_USAGE_RANGE_DAYS = 30 as const;
export const DATASET_USAGE_GROUP_BY_DAY = 'Day' as const;
// The daily rows are folded client-side into fixed 7-day buckets so the trend
// reads as a small number of dense weekly bars rather than 30 sparse daily ones.
export const DATASET_USAGE_WEEK_DAYS = 7 as const;

/** API integer enum value for annotationData (used in CreateAnnotationDataRequest). */
export const ANNOTATION_DATA_TYPE_INT = 4 as const;

// Tooltip messages for disabled actions when the user lacks the required role
export const DATASET_ROLE_TOOLTIP_ANNOTATOR =
  'You need at least Annotator role on this dataset. Contact the dataset owner.';
export const DATASET_ROLE_TOOLTIP_CONTRIBUTOR =
  'You need at least Contributor role on this dataset. Contact the dataset owner.';
export const DATASET_ROLE_TOOLTIP_OWNER =
  'Only dataset owners can manage permissions.';

/** Shown on the Overview Edit button when the dataset has no editable draft. */
export const DATASET_EDIT_TOOLTIP_NOT_DRAFT =
  'Only draft datasets can be edited. Demote to draft to edit metadata.';

// ── Test Sets ────────────────────────────────────────────────────────────────

export const TESTSET_PAGE_SUBTITLE = 'Browse and manage evaluation test sets';

export const TESTSET_OWNERSHIP_FILTERS = [
  'All Test Sets',
  'My Test Sets',
] as const;

export type TestSetOwnershipFilter = (typeof TESTSET_OWNERSHIP_FILTERS)[number];

/** Page-size options for the test set entries table. */
export const TESTSET_ENTRY_PAGE_SIZES = [10, 25, 50] as const;

export type TestSetEntryPageSize = (typeof TESTSET_ENTRY_PAGE_SIZES)[number];

// ── Workspaces ────────────────────────────────────────────────────────────────

/** Default workspace name used when no workspace is explicitly selected. */
export const DEFAULT_WORKSPACE_NAME = 'public';

// ── Graders ───────────────────────────────────────────────────────────────────

/**
 * Metadata keys written onto a grader when it is promoted from a judge
 * (grader-gen) in the Judge Factory. They record which grader-gen the grader
 * was derived from, enabling both the forward link (grader → source judge) and
 * the reverse lookup (graders created from a judge).
 *
 * `SOURCE_ENTITY_ID` is a server-side **filterable** field (a typed key on the
 * grader's metadata complex type), so the reverse lookup queries it directly via
 * `$filter=metadata/sourceEntityId eq '{id}'`. The remaining keys are display-only
 * and ride in the untyped metadata overflow.
 */
export const GRADER_METADATA_KEYS = {
  SOURCE_ENTITY_ID: 'sourceEntityId',
  SOURCE_GRADER_GEN_NAME: 'sourceGraderGenName',
  /** 1-based index of the tuning variant the grader was promoted from. */
  SOURCE_VARIANT_INDEX: 'sourceVariantIndex',
  /** Name of the tuning variant the grader was promoted from (e.g. "v1"). */
  SOURCE_VARIANT_NAME: 'sourceVariantName',
} as const;
